import React, { useContext, useState } from 'react';
import { Grid, TextField, useMediaQuery, useTheme } from '@material-ui/core';
import { withStyles } from '@material-ui/styles';
import { styled } from '@material-ui/core/styles';
import { theme } from 'theme';
import { useIntl } from 'react-intl';
import translations from 'utils/translations';
import { useHistory } from 'react-router-dom';
import { slotStatus } from 'utils/constants';
import { LoaderContext } from 'context/LoaderContextProvider';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import { VisitsContext } from 'context/VisitsContextProvider';
import { SdmSpacer, SdmTextButton, SdmTypography } from 'components';
import DeleteIcon from '@material-ui/icons/Delete';
import { useDataHelper } from 'utils/dataHelper';
import { format, intlFormat } from 'date-fns';
import DeleteDialog from './Agenda/Dialogs/DeleteDialog';
import { deleteBookingRequest } from 'entities/agenda/agendaHelper';
import { useEmailHelper } from 'services/emailSender';

const ActionLink = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  cursor: 'pointer',
});

const CustomFormTextField = withStyles({
  root: {
    width: '95%',
    marginLeft: 8,
    '& .MuiFormControl-root': {
      width: '95%',
    },
    '& label.Mui-focused': {
      color: `${theme.colors.primary.black}`,
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: `${theme.colors.primary.black}`,
    },
    '& .MuiInput-underline:before': {
      borderBottom: `2px solid ${theme.colors.primary.black}`,
    },
    '& .MuiInputBase-input': {
      fontFamily: 'Suisse-Intl',
      fontSize: 26,
    },
    '& .MuiInputBase-root.Mui-disabled': {
      color: `${theme.colors.primary.black}`,
    },
    '& .MuiFormLabel-root': {
      fontFamily: 'Suisse-Intl',
      color: `${theme.colors.primary.black}`,
      fontSize: 21,
    },
  },
})(TextField);

const MyAppointmentDetail = ({ locale }) => {
  // Hooks
  const intl = useIntl();
  const i18n = translations.Agenda(intl).operator;
  const i18nAgenda = translations.Agenda(intl).agenda.page;
  const mediaTheme = useTheme();
  const isSmDown = useMediaQuery(mediaTheme.breakpoints.down('sm'));
  const emailHelper = useEmailHelper();
  const history = useHistory();
  const dataHelper = useDataHelper();
  const { setLoaderActive } = useContext(LoaderContext);
  const { setAppointments, appointments, selectedAppointment } =
    useContext(VisitsContext);
  if (!selectedAppointment) return null;
  //Loader Function
  const showLoader = () => setLoaderActive(true);
  const hideLoader = () => setLoaderActive(false);

  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    id: '',
    isApp: false,
    customText: '',
    customSubText: '',
  });

  // Functions
  const handleBack = () => {
    if (appointments) setAppointments();
    history.push('/');
  };

  const handleDelete = () => {
    setDeleteDialog({
      isOpen: true,
      id: selectedAppointment?.id,
      isApp: false,
      customText: i18nAgenda.dialogs.delete.titleMyAgenda,
      customSubText: i18nAgenda.dialogs.delete.subMyAgenda,
    });
  };

  const removeAppointment = async () => {
    showLoader();
    try {
      const id = deleteDialog.id;
      const input = { id };
      const removed = await deleteBookingRequest({ input });
      const reasonParsed = selectedAppointment.reason;

      const referent = selectedAppointment.referent;

      const start = new Date(selectedAppointment.slot.start);
      const end = new Date(selectedAppointment.slot.end);

      const data = {
        userName: selectedAppointment.givenName,
        userLastname: selectedAppointment.familyName,
        userEmail: selectedAppointment.email,
        userSalesforceId: selectedAppointment.userSalesforceId,
        userCompany: selectedAppointment.company || 'N.A.',
        brandName: selectedAppointment.brandName,
        brandSalesforceId: selectedAppointment.brandSalesforceId,
        brandEmail: selectedAppointment.brandEmail,
        date: start.toISOString(),
        start: start.toISOString(),
        end: end.toISOString(),
        pavillion: selectedAppointment.pavillion || 'N.A.',
        stand: selectedAppointment.stand || 'N.A.',
        ...(referent && {
          referentName: `${referent.familyName} ${referent.givenName}`,
        }),
        note: reasonParsed,
      };
      await emailHelper.sendAgendaEmail({
        type: 'Delete From Operator',
        data,
      });
      setDeleteDialog({
        isOpen: false,
        id: '',
        isApp: false,
        customText: '',
        customSubText: '',
      });
      // TODO: HANDLE DELETE EMAIL NOTIFICATION
      setAppointments();
      if (removed.id) handleBack();
    } catch (error) {
      console.error('LOG::  ~ error', error);
    } finally {
      hideLoader();
    }
  };

  const handleAddCalendar = () => {
    const agendaData = {
      start: selectedAppointment?.slot.start,
      end: selectedAppointment?.slot.end,
      hall: selectedAppointment?.hall,
      stand: selectedAppointment?.stand,
      brandName: selectedAppointment?.brandName,
    };
    const iCalBlob = dataHelper.generateAgendaBlob({ agendaData });
    const customCreateObjectURL = (file) => {
      try {
        if (window.webkitURL) {
          return window.webkitURL.createObjectURL(file);
        } else if (window.URL && window.URL.createObjectURL) {
          return window.URL.createObjectURL(file);
        } else {
          return null;
        }
      } catch (e) {
        return null;
      }
    };

    const csvURL = customCreateObjectURL(iCalBlob);
    if (csvURL) {
      const tempLink = document.createElement('a');
      tempLink.href = csvURL;
      tempLink.setAttribute('download', `appointment.ics`);
      tempLink.click();
    } else {
      console.log('ERROR::');
    }
  };

  const date =
    selectedAppointment &&
    intlFormat(
      new Date(selectedAppointment?.slot.start),
      {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      },
      {
        locale: dataHelper.getLangCode(locale),
      }
    );

  const time =
    selectedAppointment &&
    `${format(new Date(selectedAppointment.slot.start), 'HH:mm')} - ${format(
      new Date(selectedAppointment.slot.end),
      'HH:mm'
    )}`;

  const renderDeleteDialog = () => {
    if (!deleteDialog.isOpen) return null;
    return (
      <DeleteDialog
        onClose={() => {
          setDeleteDialog({
            isOpen: false,
            id: '',
            isApp: false,
            customText: '',
            customSubText: '',
          });
        }}
        onAgree={() => removeAppointment()}
        i18n={i18nAgenda.dialogs.delete}
        isSmDown={isSmDown}
        customText={deleteDialog.customText || ''}
        customSubText={deleteDialog.customSubText || ''}
      />
    );
  };

  return (
    <Grid container>
      <div style={{ width: '100%' }}>
        {/* <PageHeader i18n={i18n} isBrandPage={false} /> */}
        {renderDeleteDialog()}
        <div
          style={{
            width: isSmDown ? '90%' : '80%',
            margin: '0 auto',
            maxWidth: 830,
          }}
        >
          {/* <SdmTypography
            variant={isSmDown ? 'h4Regular' : 'h2Regular'}
            style={{
              width: '100%',
              color: theme.colors.primary.black,
            }}
          >
            Lorem ipsum dolor sit amet, consectetur adipiscing elit. Praesent
            augue tortor, consectetur a dui a, blandit luctus.
          </SdmTypography> */}
          {/* <SdmSpacer height={isSmDown ? 20 : 60} /> */}
          <Grid container>
            <Grid item xs={12}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <SdmTypography
                  variant={isSmDown ? 'h4' : 'h1'}
                  style={{
                    width: '100%',
                    color: theme.colors.primary.black,
                  }}
                >
                  {i18n.fields.appointment} {selectedAppointment.brandName}
                </SdmTypography>
                <ActionLink onClick={() => handleAddCalendar()}>
                  <CalendarTodayIcon
                    style={{
                      color: theme.colors.primary.red,
                      marginRight: 8,
                    }}
                  />
                  <SdmTypography
                    variant={'h4Regular'}
                    margin={0}
                    style={{
                      color: theme.colors.primary.red,
                      marginTop: 3,
                    }}
                  >
                    {i18n.fields.addToCalendar}
                  </SdmTypography>
                </ActionLink>
              </div>
            </Grid>
            <SdmSpacer height={isSmDown ? 20 : 40} />
            <Grid item sm={6} xs={12}>
              <CustomFormTextField
                value={selectedAppointment.brandName}
                label={i18n.fields.brand}
                style={{ marginBottom: 10 }}
                disabled={true}
              />
              <SdmSpacer height={isSmDown ? 10 : 20} />
            </Grid>
            <Grid item sm={6} xs={false}></Grid>
            <Grid item sm={6} xs={12}>
              <CustomFormTextField
                value={date}
                label={i18n.fields.day}
                style={{ marginBottom: 10 }}
                disabled={true}
              />
              <SdmSpacer height={isSmDown ? 10 : 20} />
            </Grid>
            <Grid item sm={6} xs={12}>
              <CustomFormTextField
                value={time}
                label={i18n.fields.time}
                style={{ marginBottom: 10 }}
                disabled={true}
              />
              <SdmSpacer height={isSmDown ? 10 : 20} />
            </Grid>
            <Grid item sm={6} xs={12}>
              <CustomFormTextField
                value={selectedAppointment.stand || 'N.A.'}
                label={i18n.fields.stand}
                style={{ marginBottom: 10 }}
                disabled={true}
              />
              <SdmSpacer height={isSmDown ? 10 : 20} />
            </Grid>
            <Grid item sm={6} xs={12}>
              <CustomFormTextField
                value={selectedAppointment.pavillion || 'N.A.'}
                label={i18n.fields.pavillion}
                style={{ marginBottom: 10 }}
                disabled={true}
              />
              <SdmSpacer height={isSmDown ? 10 : 20} />
            </Grid>
            <Grid item sm={6} xs={12}>
              <CustomFormTextField
                value={
                  selectedAppointment.status === slotStatus.PENDING
                    ? i18n.fields.pending
                    : i18n.fields.confirmed
                }
                label={i18n.fields.status}
                style={{ marginBottom: 10 }}
                disabled={true}
              />
              <SdmSpacer height={isSmDown ? 10 : 20} />
            </Grid>
            {selectedAppointment?.referent &&
            selectedAppointment?.referent?.givenName &&
            selectedAppointment?.referent?.familyName ? (
              <Grid item sm={6} xs={12}>
                <CustomFormTextField
                  value={`${selectedAppointment.referent.givenName} ${selectedAppointment.referent.familyName}`}
                  label={i18n.fields.referent}
                  style={{ marginBottom: 10 }}
                  disabled={true}
                />
                <SdmSpacer height={isSmDown ? 10 : 20} />
              </Grid>
            ) : (
              <Grid item sm={6} xs={false} />
            )}
            <Grid item sm={12} xs={12}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: isSmDown ? 'center' : 'flex-end',
                  gap: 20,
                }}
              >
                <ActionLink onClick={() => handleDelete()}>
                  <DeleteIcon
                    style={{
                      color: theme.colors.primary.red,
                      marginRight: 8,
                    }}
                  />
                  <SdmTypography
                    variant={'h4Regular'}
                    margin={0}
                    style={{
                      color: theme.colors.primary.red,
                      marginTop: 3,
                    }}
                  >
                    {i18n.fields.deldelete}
                  </SdmTypography>
                </ActionLink>
                <SdmTextButton
                  text={i18n.fields.back}
                  height={30}
                  style={{ width: 158, height: 50, marginLeft: 10 }}
                  variant={'redFill'}
                  textVariant={'h4Regular'}
                  onClick={handleBack}
                />
              </div>
            </Grid>
          </Grid>
        </div>
      </div>
      <SdmSpacer height={isSmDown ? 20 : 80} />
    </Grid>
  );
};
export default MyAppointmentDetail;
